import React, { useImperativeHandle, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { RoleAssignmentForm } from "@/components/roles/RoleAssignmentForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { apiRequest, SUPABASE_URL } from "@/services/api/apiUtils";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";

interface InviteFormValues {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  jobTitle: string;
  phoneNumber: string;
  platformRoles: number[];
  managementCompanyRoles: { [key: string]: number };
  associationRoles: { [key: string]: number };
}

const UserInvite = () => {
  const { selectedRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<InviteFormValues>({
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      role: "",
      jobTitle: "",
      phoneNumber: "",
      platformRoles: [],
      managementCompanyRoles: {},
      associationRoles: {},
    },
  });

  const onSubmit = async (values: InviteFormValues) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      console.log(`Starting invitation process for email: ${values.email}`);

      // Check if user already exists
      const { data: existingUsers, error: checkError } = await supabase
        .from("users")
        .select("id, email, auth_user_id")
        .eq("email", values.email);

      if (checkError) {
        console.error("Error checking for existing user:", checkError);
      }

      if (existingUsers && existingUsers.length > 0) {
        console.log(
          `User with email ${values.email} already exists, attempting cleanup before reinvite`
        );

        // User exists, attempt to delete it first
        for (const user of existingUsers) {
          console.log(
            `Deleting existing user with ID ${user.id} and auth_user_id ${
              user.auth_user_id || "none"
            }`
          );

          // Delete platform role assignments
          const { error: platformRoleError } = await supabase
            .from("platform_role_assignments")
            .delete()
            .eq("user_id", user.id);

          if (platformRoleError) {
            console.warn("Error deleting platform roles:", platformRoleError);
          }

          // Delete management company role assignments
          const { error: mcRoleError } = await supabase
            .from("management_company_role_assignments")
            .delete()
            .eq("user_id", user.id);

          if (mcRoleError) {
            console.warn(
              "Error deleting management company roles:",
              mcRoleError
            );
          }

          // Delete association role assignments
          const { error: assocRoleError } = await supabase
            .from("association_role_assignments")
            .delete()
            .eq("user_id", user.id);

          if (assocRoleError) {
            console.warn("Error deleting association roles:", assocRoleError);
          }

          // Delete the user from users table
          const { data: deletedRows, error: userDeleteError } = await supabase
            .from("users")
            .delete()
            .eq("id", user.id)
            .select("id");

          if (userDeleteError) {
            console.error("Error deleting user record:", userDeleteError);
            throw new Error(
              `Could not delete existing user: ${userDeleteError.message}`
            );
          }

          // RLS filters a delete it does not allow instead of failing it. Stop
          // here, or the auth account below would be removed while the row stays.
          if (!deletedRows || deletedRows.length === 0) {
            throw new Error(
              "Could not delete existing user: you do not have permission to remove this user."
            );
          }

          // Delete auth user if auth_user_id exists
          if (user.auth_user_id) {
            try {
              await apiRequest(`${SUPABASE_URL}/users`, {
                method: "POST",
                body: {
                  action: "delete-user",
                  authUserId: user.auth_user_id,
                },
              });
            } catch (authError) {
              console.warn(
                "Error deleting auth user (continuing anyway):",
                authError
              );
            }
          }
        }
      }

      // Now invite the user. Route through apiRequest (not
      // supabase.functions.invoke) so the X-User-Role header travels — the
      // backoffice functions now require it, and invoke() does not send it.
      console.log(`Inviting user with email: ${values.email}`);
      const data = await apiRequest<{ userId?: string }>(
        `${SUPABASE_URL}/invite-user`,
        {
          method: "POST",
          body: {
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            role: values.role,
            jobTitle: values.jobTitle,
            phoneNumber: values.phoneNumber,
          },
        }
      );

      if (!data || !data.userId) {
        console.error("No user ID returned from invitation:", data);
        throw new Error("Failed to create user account");
      }

      console.log(`User created with ID: ${data.userId}, assigning roles...`);

      // Assign platform roles if any are selected
      for (const roleId of values.platformRoles) {
        await apiRequest(`${SUPABASE_URL}/users`, {
          method: "POST",
          body: {
            userId: data.userId,
            action: "assign-platform-role",
            roleId,
          },
        });
      }

      // Assign management company roles
      for (const [companyId, roleId] of Object.entries(
        values.managementCompanyRoles
      )) {
        await apiRequest(`${SUPABASE_URL}/users`, {
          method: "POST",
          body: {
            userId: data.userId,
            action: "assign-management-company-role",
            roleId,
            managementCompanyId: parseInt(companyId),
          },
        });
      }

      // Assign association roles
      for (const [associationId, roleId] of Object.entries(
        values.associationRoles
      )) {
        await apiRequest(`${SUPABASE_URL}/users`, {
          method: "POST",
          body: {
            userId: data.userId,
            action: "assign-association-role",
            roleId,
            associationId: parseInt(associationId),
          },
        });
      }

      toast({
        title: "User invited successfully",
        description:
          "The user will receive an email with instructions to set up their account.",
      });

      navigate("/backoffice/users");
    } catch (error) {
      console.error("Error inviting user:", error);

      // Handle specific error messages
      let errorMsg = "Failed to invite user. Please try again.";

      if (error.message) {
        if (
          error.message.includes("duplicate key value") ||
          error.message.includes("already exists") ||
          error.message.includes("already registered")
        ) {
          errorMsg = "A user with this email address already exists.";
        } else {
          errorMsg = error.message;
        }
      }

      setErrorMessage(errorMsg);

      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleUpdate = (
    entityType: "platform" | "management_company" | "association",
    entityId: number | null,
    roleId: number | null
  ) => {
    const fieldName =
      entityType === "platform"
        ? "platformRoles"
        : entityType === "management_company"
        ? "managementCompanyRoles"
        : "associationRoles";

    if (entityType === "platform") {
      if (roleId) {
        form.setValue(fieldName as "platformRoles", [
          ...(form.getValues("platformRoles") || []),
          roleId,
        ]);
      }
    } else {
      if (entityId && roleId) {
        form.setValue(
          fieldName as "managementCompanyRoles" | "associationRoles",
          {
            ...form.getValues(
              fieldName as "managementCompanyRoles" | "associationRoles"
            ),
            [entityId]: roleId,
          }
        );
      }
    }
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Invite New User</CardTitle>
          </CardHeader>
          <CardContent>
            {errorMessage && (
              <Alert variant="destructive" className="mb-6">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-8"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter email address"
                          type="email"
                          required
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter first name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter last name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Role</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Manager, Administrator"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="jobTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Job Title</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Application Coordinator"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="e.g., (555) 123-4567"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="space-y-6">
                  {selectedRole?.type === "platform" && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Platform Roles</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <RoleAssignmentForm
                          userId={-1}
                          entityType="platform"
                          onRoleAssigned={(roleId) =>
                            handleRoleUpdate("platform", null, roleId)
                          }
                        />
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle>Management Company Roles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RoleAssignmentForm
                        userId={-1}
                        entityType="management_company"
                        onRoleAssigned={(roleId, entityId) =>
                          handleRoleUpdate(
                            "management_company",
                            entityId,
                            roleId
                          )
                        }
                      />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Association Roles</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <RoleAssignmentForm
                        userId={-1}
                        entityType="association"
                        onRoleAssigned={(roleId, entityId) =>
                          handleRoleUpdate("association", entityId, roleId)
                        }
                      />
                    </CardContent>
                  </Card>
                </div>

                <Button type="submit" disabled={isLoading} className="w-full">
                  {isLoading && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Send Invitation
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </BackofficeLayout>
  );
};

export default UserInvite;
